import { useEffect, useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Tooltip } from '@fluentui/react-components';
import { useLicense } from '../hooks/useLicense';
import {
  ShieldCheckmark24Regular,
  Info16Regular,
  ArrowSync20Regular,
  DataArea24Regular,
  Table24Regular,
  Bug24Regular,
  ChartMultiple24Regular,
} from '@fluentui/react-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar,
} from 'recharts';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import PageHero from '../components/layout/PageHero';
import { CopilotDataTable } from '../components/copilot/CopilotDataTable';
import { CopilotDebugInspector } from '../components/copilot/CopilotDebugInspector';
import { LocalIndexService } from '../services/LocalIndexService';
import { coreSettingsService } from '../services/CoreSettingsService';
import { AuthSessionExpiredError } from '../services/authService';
import { trackFeatureUsage, trackException, FeatureEvent, EventProp, ExceptionPhase, ExceptionProp } from '../observability';

// ─── Radial Gauge ──────────────────────────────────────────────────────────────
function ReadinessGauge({ value }: { value: number }) {
  const clamped = Math.min(100, Math.max(0, value));
  const radius = 56;
  const cx = 72,
    cy = 72;
  const circumference = Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const color =
    clamped >= 75 ? '#059669' : clamped >= 50 ? '#d97706' : '#dc2626';
  const ringBg =
    clamped >= 75 ? '#d1fae5' : clamped >= 50 ? '#fef3c7' : '#fee2e2';
  const label =
    clamped >= 75 ? 'Great Shape' : clamped >= 50 ? 'Needs Work' : 'At Risk';

  return (
    <div className='flex flex-col items-center gap-2'>
      <svg width='144' height='88' viewBox='0 0 144 88'>
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill='none'
          stroke={ringBg}
          strokeWidth='11'
          strokeLinecap='round'
        />
        <path
          d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
          fill='none'
          stroke={color}
          strokeWidth='11'
          strokeLinecap='round'
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition:
              'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1), stroke 0.5s',
          }}
        />
        <text
          x={cx}
          y={cy - 8}
          textAnchor='middle'
          fontSize='24'
          fontWeight='900'
          fill='white'
        >
          {clamped.toFixed(0)}%
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor='middle'
          fontSize='11'
          fill='rgba(255,255,255,0.7)'
          fontWeight='600'
        >
          {label}
        </text>
      </svg>
      <span className='text-xs text-white/50 tracking-wide uppercase'>
        Overall AI Readiness
      </span>
    </div>
  );
}

// ─── Score Tile removed (metrics are now inline in hero KPI strip) ──────────────

// ─── Readiness Story Card ────────────────────────────────────────────────────────
const QUADRANT_META = [
  {
    key: 'Best Practice',
    color: '#059669',
    label: 'Best Practice',
    desc: 'Clear & Coherent',
    advice: 'AI-ready. Use as content templates.',
  },
  {
    key: 'Needs Simplification',
    color: '#2ba6bb',
    label: 'Needs Simplification',
    desc: 'Well Structured, Complex',
    advice: 'Good flow. Simplify language.',
  },
  {
    key: 'Needs Structure',
    color: '#d97706',
    label: 'Needs Structure',
    desc: 'Readable, but Disjointed',
    advice: 'Easy to read. Add headings & flow.',
  },
  {
    key: 'Major Revision',
    color: '#dc2626',
    label: 'Major Revision',
    desc: 'Hard to Read & Disjointed',
    advice: 'Will confuse Copilot. Revise first.',
  },
];

function ReadinessStoryCard({ items }: { items: any[] }) {
  if (items.length === 0) return null;

  const total = items.length;
  const counts = QUADRANT_META.map((q) => ({
    ...q,
    count: items.filter((i) => i.MatrixQuadrant === q.key).length,
  }));
  const scored = counts.reduce((a, c) => a + c.count, 0);

  // Recharts wants [{name,value,color}]
  const pieData = counts.map((c) => ({
    name: c.label,
    value: c.count || 0.001,
    color: c.color,
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    const real = counts.find((c) => c.label === d.name);
    if (!real) return null;
    return (
      <div className='bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-sm'>
        <p className='font-bold' style={{ color: real.color }}>
          {real.label}
        </p>
        <p className='text-gray-600'>
          {real.count} pages ·{' '}
          {total > 0 ? Math.round((real.count / total) * 100) : 0}%
        </p>
        <p className='text-gray-400 text-xs mt-1'>{real.advice}</p>
      </div>
    );
  };

  return (
    <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
      {/* Header */}
      <div className='px-6 py-5 border-b border-gray-100 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <span className='p-2 bg-[#e0f4f7] rounded-xl text-[#2ba6bb]'>
            <ChartMultiple24Regular />
          </span>
          <div>
            <h3 className='text-base font-bold text-gray-900'>
              Page Readiness Map
            </h3>
            <p className='text-sm text-gray-500'>
              {scored > 0
                ? `${scored} of ${total} pages classified`
                : 'Run a scan to classify your pages'}
            </p>
          </div>
        </div>
        <span className='text-xs text-gray-400 hidden sm:block'>
          How ready is your content for Microsoft Copilot?
        </span>
      </div>

      {/* Body: stat list (left) + donut (right) */}
      <div className='flex flex-col md:flex-row'>
        {/* Left — stat rows */}
        <div className='flex-1 divide-y divide-gray-50 py-2'>
          {counts.map((q) => {
            const pct = total > 0 ? Math.round((q.count / total) * 100) : 0;
            return (
              <div
                key={q.key}
                className='flex items-center gap-4 px-6 py-4 hover:bg-gray-50/60 transition-colors group'
              >
                {/* Colour dot + count */}
                <div className='flex items-center gap-3 shrink-0'>
                  <div
                    className='w-3 h-3 rounded-full'
                    style={{ backgroundColor: q.color }}
                  />
                  <span
                    className='text-2xl font-black w-8 text-right'
                    style={{ color: q.color }}
                  >
                    {q.count}
                  </span>
                </div>
                {/* Label + desc + bar */}
                <div className='flex-1 min-w-0'>
                  <div className='flex items-baseline justify-between gap-2 mb-1'>
                    <span className='text-sm font-bold text-gray-800'>
                      {q.label}
                    </span>
                    <span className='text-xs text-gray-400 shrink-0'>
                      {pct}%
                    </span>
                  </div>
                  <div className='h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                    <div
                      className='h-1.5 rounded-full transition-all duration-700'
                      style={{ width: `${pct}%`, backgroundColor: q.color }}
                    />
                  </div>
                  <p className='text-xs text-gray-400 mt-1 leading-relaxed'>
                    {q.desc}
                  </p>
                </div>
                {/* Advice on hover */}
                <p
                  className='text-xs font-medium max-w-[160px] text-right hidden xl:block opacity-0 group-hover:opacity-100 transition-opacity'
                  style={{ color: q.color }}
                >
                  {q.advice}
                </p>
              </div>
            );
          })}
        </div>

        {/* Right — Recharts donut */}
        <div className='w-full md:w-64 lg:w-72 flex flex-col items-center justify-center py-8 px-4 border-t md:border-t-0 md:border-l border-gray-100 bg-gray-50/30'>
          <p className='text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3'>
            Distribution
          </p>
          <ResponsiveContainer width='100%' height={180}>
            <PieChart>
              <Pie
                data={pieData}
                cx='50%'
                cy='50%'
                innerRadius={52}
                outerRadius={80}
                paddingAngle={scored > 0 ? 3 : 0}
                dataKey='value'
                strokeWidth={0}
                animationBegin={0}
                animationDuration={900}
              >
                {pieData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.color}
                    opacity={entry.value < 0.01 ? 0.15 : 1}
                  />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className='flex flex-col gap-1.5 w-full px-2'>
            {counts.map((q) => (
              <div
                key={q.key}
                className='flex items-center justify-between text-xs'
              >
                <div className='flex items-center gap-2'>
                  <span
                    className='w-2.5 h-2.5 rounded-sm inline-block shrink-0'
                    style={{ backgroundColor: q.color }}
                  />
                  <span className='text-gray-600 font-medium truncate max-w-[120px]'>
                    {q.label}
                  </span>
                </div>
                <span className='font-bold ml-2' style={{ color: q.color }}>
                  {q.count}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 8-Point Quality Checks ───────────────────────────────────────────────────────
const CHECKS = [
  {
    field: 'writeForPeopleFirst',
    label: 'No AI Writing',
    icon: '✍️',
    desc: 'No AI-specific writing patterns detected',
  },
  {
    field: 'complexLayoutPenalized',
    label: 'Simple Layout',
    icon: '📐',
    desc: 'Tag-to-text density within acceptable range',
    invert: true,
  },
  {
    field: 'shortSections',
    label: 'Short Sections',
    icon: '📄',
    desc: 'No oversize (>450 token) content blocks',
  },
  {
    field: 'clearHeadings',
    label: 'Good Headings',
    icon: '🎯',
    desc: 'H2/H3 headings present and well distributed',
  },
  {
    field: 'metadataPresent',
    label: 'Has Metadata',
    icon: '🏷️',
    desc: 'Page metadata and description are filled in',
  },
  {
    field: 'imagesHaveAltText',
    label: 'Alt Text',
    icon: '🖼️',
    desc: 'All images have alternative text',
  },
  {
    field: 'hasStrongSummary',
    label: 'Clear Intro',
    icon: '📝',
    desc: 'First paragraph summarises the page purpose',
  },
  {
    field: 'noKeywordStuffing',
    label: 'Natural Language',
    icon: '🔍',
    desc: 'No keyword stuffing signals detected',
  },
];

function QualityStrip({ items }: { items: any[] }) {
  if (items.length === 0) return null;
  const scanned = items.filter(
    (i) => i.AnalysisReport && Object.keys(i.AnalysisReport).length > 0,
  );
  if (scanned.length === 0) return null;

  const results = CHECKS.map((c) => {
    let pass = 0;
    for (const item of scanned) {
      const val = item.AnalysisReport[c.field];
      if (c.invert ? !val : !!val) pass++;
    }
    const pct = Math.round((pass / scanned.length) * 100);
    return { ...c, pass, pct };
  }).sort((a, b) => a.pct - b.pct); // worst first

  const passCount = results.filter((r) => r.pct >= 75).length;
  const overallPct = Math.round(
    results.reduce((a, r) => a + r.pct, 0) / results.length,
  );

  return (
    <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-gray-100 flex items-center justify-between'>
        <div>
          <h3 className='text-base font-bold text-gray-900'>
            Content Quality Checks
          </h3>
          <p className='text-sm text-gray-500 mt-0.5'>
            Sorted by urgency, most in need of attention first
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <div className='text-right hidden sm:block'>
            <p className='text-xs text-gray-400'>Overall pass rate</p>
            <p
              className='text-lg font-black'
              style={{
                color:
                  overallPct >= 75
                    ? '#059669'
                    : overallPct >= 50
                      ? '#d97706'
                      : '#dc2626',
              }}
            >
              {passCount}/{results.length} checks
            </p>
          </div>
          <span className='text-xs font-semibold text-[#2ba6bb] bg-[#e0f4f7] px-3 py-1.5 rounded-full'>
            {scanned.length} pages
          </span>
        </div>
      </div>

      {/* Sorted rows — full width, no columns */}
      <div className='divide-y divide-gray-50'>
        {results.map((c, idx) => {
          const color =
            c.pct >= 75 ? '#059669' : c.pct >= 45 ? '#d97706' : '#dc2626';
          const bgIcon =
            c.pct >= 75 ? '#f0fdf4' : c.pct >= 45 ? '#fffbeb' : '#fef2f2';
          const statusLabel =
            c.pct >= 75 ? 'Good' : c.pct >= 45 ? 'Needs Work' : 'At Risk';
          const isUrgent = c.pct < 45;
          return (
            <div
              key={c.field}
              className={`flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-gray-50/50 transition-colors ${isUrgent && idx === 0 ? 'bg-red-50/30' : ''}`}
            >
              {/* Rank */}
              <span className='text-xs font-bold text-gray-300 w-4 shrink-0 text-right'>
                {idx + 1}
              </span>
              {/* Icon */}
              <div
                className='w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center text-base sm:text-lg shrink-0'
                style={{ backgroundColor: bgIcon }}
              >
                {c.icon}
              </div>
              {/* Content */}
              <div className='flex-1 min-w-0'>
                <div className='flex items-center justify-between gap-2 mb-1.5'>
                  <span className='text-sm font-bold text-gray-800 truncate'>
                    {c.label}
                  </span>
                  <div className='flex items-center gap-1.5 shrink-0'>
                    <span className='text-xs text-gray-400 hidden sm:inline whitespace-nowrap'>
                      {c.pass}/{scanned.length} pages
                    </span>
                    <span
                      className='text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap'
                      style={{ backgroundColor: color + '18', color }}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
                <div className='h-2 bg-gray-100 rounded-full overflow-hidden'>
                  <div
                    className='h-2 rounded-full transition-all duration-700'
                    style={{ width: `${c.pct}%`, backgroundColor: color }}
                  />
                </div>
                <span className='text-xs text-gray-400 sm:hidden mt-0.5 block'>
                  {c.pass}/{scanned.length} pages
                </span>
              </div>
              {/* Pct */}
              <div
                className='text-base sm:text-lg font-black shrink-0 w-10 sm:w-12 text-right'
                style={{ color }}
              >
                {c.pct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [processedCount, setProcessedCount] = useState(0);
  const [scanPhase, setScanPhase] = useState<
    'idle' | 'fetching' | 'analyzing' | 'saving'
  >('idle');
  const [items, setItems] = useState<any[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'pages' | 'debug'>(
    'overview',
  );
  const [sessionExpired, setSessionExpired] = useState(false);

  const indexService = useMemo(() => new LocalIndexService(), []);

  const { license, isLoading: licenseLoading } = useLicense();

  // Use the stored primarySiteUrl from the licence record (WVHealth_ToolConfig).
  // This is the source of truth — same pattern as AdminSettingsPage.
  // Falling back to a root-site URL derived from the user's email was causing
  // WV_SearchEmbeddingsCache and page scans to target the wrong site.
  const siteUrl = license?.allowedSiteUrls?.[0] || '';

  useEffect(() => {
    if (accounts.length === 0) navigate('/');
  }, [accounts, navigate]);

  const loadData = async (forceRefresh = false) => {
    indexService.reset();
    setSessionExpired(false);
    setIsProcessing(true);
    setProcessedCount(0);
    setScanPhase('fetching');
    setProgressMsg('Fetching site pages from SharePoint…');
    trackFeatureUsage(FeatureEvent.DashboardScanStarted, { [EventProp.ForceRefresh]: String(forceRefresh) });
    try {
      indexService.onProgress = (msg) => {
        setProgressMsg(msg);
        // Derive phase and count from progress messages
        if (msg.startsWith('Fetching') || msg.startsWith('Loading')) {
          setScanPhase('fetching');
        } else if (
          msg.startsWith('Processing') ||
          msg.startsWith('Processed:')
        ) {
          setScanPhase('analyzing');
          // Extract count from "Processed: Page Title" — count every update
          setProcessedCount((prev) => prev + 1);
          // Also update items live so partial results show up
          setItems(indexService.getRawIndex());
        } else if (msg.startsWith('Saving') || msg.startsWith('All ML')) {
          setScanPhase('saving');
        } else if (msg.startsWith('Checking') || msg.startsWith('Fetched')) {
          setScanPhase('fetching');
          // Extract fetched count if available
          const match = msg.match(/(\d+) pages/);
          if (match) setProcessedCount(0); // reset counter when fetch reported
        }
      };
      const config = await coreSettingsService.getSettings(siteUrl);
      await indexService.initializeLocalIndex(siteUrl, {
        cachingStrategy: config.cachingStrategy,
        modelId: config.modelId,
        maxTotalIndexItems: config.maxTotalIndexItems,
        forceRefresh: forceRefresh,
      });
      setItems(indexService.getRawIndex());
      setLastUpdated(new Date());
      trackFeatureUsage(FeatureEvent.DashboardScanCompleted, { [EventProp.PageCount]: String(indexService.getRawIndex().length) });
    } catch (err) {
      if (err instanceof AuthSessionExpiredError) {
        setSessionExpired(true);
      } else {
        console.error('Dashboard init error', err);
        trackException(err, { [ExceptionProp.Phase]: ExceptionPhase.DashboardScan });
      }
    } finally {
      setIsProcessing(false);
      setScanPhase('idle');
    }
  };

  // Trigger initial load once the licence has resolved and we have a siteUrl
  useEffect(() => {
    if (!licenseLoading && siteUrl) loadData(false);
  }, [licenseLoading, siteUrl]);

  const userName =
    accounts[0]?.name || accounts[0]?.username?.split('@')[0] || 'User';

  const metrics = useMemo(() => {
    if (!items || items.length === 0)
      return {
        totalPages: 0,
        avgReadability: 0,
        avgStructure: 0,
        overallReadiness: 0,
      };
    const n = items.length;
    return {
      totalPages: n,
      avgReadability:
        items.reduce((a, c) => a + (Number(c.Readability) || 0), 0) / n,
      avgStructure:
        items.reduce((a, c) => a + (Number(c.StructureScore) || 0), 0) / n,
      overallReadiness:
        items.reduce((a, c) => a + (Number(c.Readiness) || 0), 0) / n,
    };
  }, [items]);

  const TABS = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <DataArea24Regular className='w-5 h-5' />,
      badge: null,
      badgeActive: 'bg-[#2ba6bb] text-white',
      activeColor: 'border-[#2ba6bb] text-[#2ba6bb]',
    },
    {
      id: 'pages',
      label: 'All Pages',
      icon: <Table24Regular className='w-5 h-5' />,
      badge: items.length > 0 ? items.length : null,
      badgeActive: 'bg-[#2ba6bb] text-white',
      activeColor: 'border-[#2ba6bb] text-[#2ba6bb]',
    },
    {
      id: 'debug',
      label: 'Expert Inspector',
      icon: <Bug24Regular className='w-5 h-5' />,
      badge: null,
      badgeActive: 'bg-amber-500 text-white',
      activeColor: 'border-amber-500 text-amber-600',
    },
  ] as const;

  return (
    <div className='min-h-screen flex flex-col bg-[#f0f4f8]'>
      <Header
        isAuthenticated={true}
        userName={userName}
        onSignOut={() => instance.logoutPopup()}
        siteUrl={siteUrl}
        localIndexService={indexService}
      />

      <PageHero
        icon={<ChartMultiple24Regular style={{ width: 22, height: 22 }} />}
        eyebrow={{
          icon: <ShieldCheckmark24Regular style={{ width: 16, height: 16 }} />,
          label: 'Content Trust Platform',
        }}
        title={
          <>
            Content Assessment Tool (CAT)
            <span className='block text-[#2ba6bb]'>Dashboard</span>
          </>
        }
        subtitle='Automatically scans your SharePoint intranet and tells you exactly which pages are ready for Microsoft Copilot, and which ones need attention first.'
        belowSubtitle={
          lastUpdated && (
            <p className='text-blue-300/50 text-xs mt-3 flex items-center gap-1.5'>
              <span className='inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse' />
              Last scan: {lastUpdated.toLocaleString()}
            </p>
          )
        }
        rightSlot={
          <>
            {isProcessing ? (
              // ── Rich Scan Progress Panel (replaces gauge during scan) ──
              <div className='flex flex-col items-center gap-4 min-w-[260px]'>
                {/* Animated ring */}
                <div className='relative w-[120px] h-[120px]'>
                  <svg
                    className='w-full h-full -rotate-90'
                    viewBox='0 0 120 120'
                  >
                    <circle
                      cx='60'
                      cy='60'
                      r='52'
                      fill='none'
                      stroke='rgba(255,255,255,0.08)'
                      strokeWidth='10'
                    />
                    <circle
                      cx='60'
                      cy='60'
                      r='52'
                      fill='none'
                      stroke='#2ba6bb'
                      strokeWidth='10'
                      strokeLinecap='round'
                      strokeDasharray='326.7'
                      strokeDashoffset={
                        scanPhase === 'fetching'
                          ? 245
                          : scanPhase === 'analyzing'
                            ? 163
                            : 50
                      }
                      style={{
                        transition:
                          'stroke-dashoffset 1.2s cubic-bezier(.4,0,.2,1)',
                      }}
                    />
                  </svg>
                  <div className='absolute inset-0 flex flex-col items-center justify-center'>
                    <span className='text-3xl font-black text-white leading-none'>
                      {processedCount > 0 ? (
                        processedCount
                      ) : (
                        <svg
                          className='animate-spin w-8 h-8 text-[#2ba6bb]'
                          fill='none'
                          viewBox='0 0 24 24'
                        >
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'
                          />
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                          />
                        </svg>
                      )}
                    </span>
                    {processedCount > 0 && (
                      <span className='text-xs text-white/50 mt-0.5'>
                        pages
                      </span>
                    )}
                  </div>
                </div>

                {/* Phase label */}
                <div className='text-center'>
                  <p className='text-white font-bold text-sm'>
                    {scanPhase === 'fetching' && '📥 Fetching Pages…'}
                    {scanPhase === 'analyzing' && '🧠 Analysing with AI…'}
                    {scanPhase === 'saving' && '💾 Saving Results…'}
                    {scanPhase === 'idle' && 'Starting…'}
                  </p>
                  <p
                    className='text-white/50 text-xs mt-1 max-w-[220px] truncate'
                    title={progressMsg}
                  >
                    {progressMsg || 'Please wait…'}
                  </p>
                </div>

                {/* Animated progress bar */}
                <div className='w-[220px] h-1.5 bg-white/10 rounded-full overflow-hidden'>
                  <div
                    className='h-1.5 rounded-full bg-[#2ba6bb] transition-all duration-700'
                    style={{
                      width:
                        scanPhase === 'fetching'
                          ? '25%'
                          : scanPhase === 'analyzing'
                            ? `${Math.min(95, 25 + (processedCount / Math.max(1, items.length || 100)) * 70)}%`
                            : scanPhase === 'saving'
                              ? '98%'
                              : '5%',
                    }}
                  />
                </div>

                <p className='text-white/30 text-[10px] text-center max-w-[200px]'>
                  Large sites with 500+ pages may take several minutes.
                  <br />
                  Results appear as pages are processed.
                </p>
              </div>
            ) : (
              <ReadinessGauge value={metrics.overallReadiness} />
            )}
            <button
              onClick={() => loadData(true)}
              disabled={isProcessing}
              className='flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all
                                    bg-[#2ba6bb] hover:bg-[#228a9d] text-white shadow-lg shadow-[#2ba6bb]/30 
                                    disabled:opacity-50 disabled:cursor-not-allowed active:scale-95'
            >
              <ArrowSync20Regular
                className={isProcessing ? 'animate-spin' : ''}
              />
              {isProcessing ? 'Scanning…' : 'Re-scan Pages'}
            </button>
          </>
        }
      >
        {/* KPI Strip — hidden during scan, shows live partial results while processing */}
        {items.length > 0 &&
          (() => {
            const analyzed = items.filter(
              (i) =>
                i.AnalysisReport && Object.keys(i.AnalysisReport).length > 0,
            ).length;
            const needRevision = items.filter(
              (i) => i.MatrixQuadrant === 'Major Revision',
            ).length;
            const readabilityColor =
              metrics.avgReadability >= 60
                ? '#059669'
                : metrics.avgReadability >= 40
                  ? '#d97706'
                  : '#dc2626';
            const structureColor =
              metrics.avgStructure >= 50
                ? '#059669'
                : metrics.avgStructure >= 30
                  ? '#d97706'
                  : '#dc2626';
            const revisionColor =
              needRevision === 0
                ? '#059669'
                : needRevision <= 2
                  ? '#d97706'
                  : '#dc2626';

            return (
              <div className='grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8'>
                {/* Card 1 — Pages Scanned: BarChart */}
                <div
                  className='bg-white rounded-2xl px-5 py-4 shadow-lg'
                  style={{ borderLeft: '4px solid #2ba6bb' }}
                >
                  <div className='flex items-start justify-between'>
                    <div>
                      <div className='text-3xl font-black text-gray-900 leading-none'>
                        {metrics.totalPages}
                      </div>
                      <div className='text-sm font-bold text-gray-700 mt-1'>
                        Pages Scanned
                      </div>
                      <div className='text-xs text-gray-400 mt-0.5'>
                        {analyzed} fully analyzed
                      </div>
                    </div>
                    <div className='shrink-0' style={{ width: 64, height: 48 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <BarChart
                          data={[
                            { a: analyzed, b: metrics.totalPages - analyzed },
                          ]}
                          barCategoryGap={0}
                          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                        >
                          <Bar
                            dataKey='a'
                            fill='#2ba6bb'
                            radius={[3, 3, 0, 0]}
                          />
                          <Bar
                            dataKey='b'
                            fill='#e0f4f7'
                            radius={[3, 3, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className='mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                    <div
                      className='h-1.5 rounded-full bg-[#2ba6bb] transition-all duration-700'
                      style={{
                        width: `${metrics.totalPages > 0 ? (analyzed / metrics.totalPages) * 100 : 0}%`,
                      }}
                    />
                  </div>
                  <div className='text-right text-xs text-gray-400 mt-0.5'>
                    {metrics.totalPages > 0
                      ? Math.round((analyzed / metrics.totalPages) * 100)
                      : 0}
                    % complete
                  </div>
                </div>

                {/* Card 2 — Avg. Readability: RadialBarChart */}
                <div
                  className='bg-white rounded-2xl px-5 py-4 shadow-lg'
                  style={{ borderLeft: `4px solid ${readabilityColor}` }}
                >
                  <div className='flex items-start justify-between'>
                    <div>
                      <div
                        className='text-3xl font-black leading-none'
                        style={{ color: readabilityColor }}
                      >
                        {metrics.avgReadability.toFixed(1)}
                      </div>
                      <div className='text-sm font-bold text-gray-700 mt-1'>
                        Avg. Readability
                      </div>
                      <div
                        className='text-xs mt-0.5'
                        style={{ color: readabilityColor }}
                      >
                        {metrics.avgReadability >= 60
                          ? '✓ On target (60+)'
                          : '⚠ Target is 60+'}
                      </div>
                    </div>
                    <div className='shrink-0' style={{ width: 56, height: 56 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <RadialBarChart
                          innerRadius='70%'
                          outerRadius='100%'
                          startAngle={210}
                          endAngle={-30}
                          data={[
                            {
                              value: Math.min(100, metrics.avgReadability),
                              fill: readabilityColor,
                            },
                          ]}
                          barSize={8}
                        >
                          <RadialBar
                            dataKey='value'
                            background={{ fill: '#f3f4f6' }}
                            cornerRadius={4}
                          />
                        </RadialBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className='mt-3 flex items-center gap-1.5'>
                    <div className='flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                      <div
                        className='h-1.5 rounded-full transition-all duration-700'
                        style={{
                          width: `${Math.min(100, metrics.avgReadability)}%`,
                          backgroundColor: readabilityColor,
                        }}
                      />
                    </div>
                    <span className='text-xs text-gray-400 shrink-0'>/100</span>
                  </div>
                </div>

                {/* Card 3 — Avg. Structure: half-donut */}
                <div
                  className='bg-white rounded-2xl px-5 py-4 shadow-lg'
                  style={{ borderLeft: `4px solid ${structureColor}` }}
                >
                  <div className='flex items-start justify-between'>
                    <div>
                      <div
                        className='text-3xl font-black leading-none'
                        style={{ color: structureColor }}
                      >
                        {metrics.avgStructure.toFixed(0)}
                        <span className='text-base font-semibold text-gray-400 ml-0.5'>
                          %
                        </span>
                      </div>
                      <div className='text-sm font-bold text-gray-700 mt-1'>
                        Avg. Structure
                      </div>
                      <div
                        className='text-xs mt-0.5'
                        style={{ color: structureColor }}
                      >
                        {metrics.avgStructure >= 50
                          ? '✓ Good density'
                          : '⚠ Add headings'}
                      </div>
                    </div>
                    <div className='shrink-0' style={{ width: 56, height: 56 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                          <Pie
                            data={[
                              {
                                value: metrics.avgStructure,
                                fill: structureColor,
                              },
                              {
                                value: 100 - metrics.avgStructure,
                                fill: '#f3f4f6',
                              },
                            ]}
                            cx='50%'
                            cy='100%'
                            startAngle={180}
                            endAngle={0}
                            innerRadius={18}
                            outerRadius={28}
                            dataKey='value'
                            strokeWidth={0}
                          >
                            <Cell fill={structureColor} />
                            <Cell fill='#f3f4f6' />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className='mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                    <div
                      className='h-1.5 rounded-full transition-all duration-700'
                      style={{
                        width: `${metrics.avgStructure}%`,
                        backgroundColor: structureColor,
                      }}
                    />
                  </div>
                </div>

                {/* Card 4 — Need Revision: proportion ring */}
                <div
                  className='bg-white rounded-2xl px-5 py-4 shadow-lg'
                  style={{ borderLeft: `4px solid ${revisionColor}` }}
                >
                  <div className='flex items-start justify-between'>
                    <div>
                      <div
                        className='text-3xl font-black leading-none'
                        style={{ color: revisionColor }}
                      >
                        {needRevision}
                        <span className='text-base font-semibold text-gray-400 ml-1'>
                          pages
                        </span>
                      </div>
                      <div className='text-sm font-bold text-gray-700 mt-1'>
                        Need Revision
                      </div>
                      <div
                        className='text-xs mt-0.5'
                        style={{ color: revisionColor }}
                      >
                        {needRevision === 0
                          ? '✓ All pages OK'
                          : 'Low coherence & readability'}
                      </div>
                    </div>
                    <div className='shrink-0' style={{ width: 56, height: 56 }}>
                      <ResponsiveContainer width='100%' height='100%'>
                        <PieChart>
                          <Pie
                            data={[
                              {
                                value: needRevision || 0.001,
                                fill: revisionColor,
                              },
                              {
                                value:
                                  Math.max(
                                    0,
                                    metrics.totalPages - needRevision,
                                  ) || 0.001,
                                fill: '#f3f4f6',
                              },
                            ]}
                            cx='50%'
                            cy='50%'
                            innerRadius={16}
                            outerRadius={26}
                            dataKey='value'
                            strokeWidth={0}
                            paddingAngle={needRevision > 0 ? 3 : 0}
                          >
                            <Cell fill={revisionColor} />
                            <Cell fill='#f3f4f6' />
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className='mt-3 h-1.5 bg-gray-100 rounded-full overflow-hidden'>
                    <div
                      className='h-1.5 rounded-full transition-all duration-700'
                      style={{
                        width: `${metrics.totalPages > 0 ? (needRevision / metrics.totalPages) * 100 : 0}%`,
                        backgroundColor: revisionColor,
                      }}
                    />
                  </div>
                  <div className='text-right text-xs text-gray-400 mt-0.5'>
                    {metrics.totalPages > 0
                      ? Math.round((needRevision / metrics.totalPages) * 100)
                      : 0}
                    % of total
                  </div>
                </div>
              </div>
            );
          })()}
      </PageHero>

      <main className='flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 space-y-5'>
        {/* Session Expired */}
        {sessionExpired && (
          <div className='flex items-start gap-4 bg-gray-900 border border-[#2ba6bb]/30 border-l-4 border-l-[#2ba6bb] rounded-2xl p-5 shadow'>
            <div className='w-10 h-10 shrink-0 rounded-full bg-[#2ba6bb]/15 flex items-center justify-center text-xl'>
              🔐
            </div>
            <div className='flex-1'>
              <p className='font-bold text-white text-sm mb-1'>
                Session Expired: Please Sign In Again
              </p>
              <p className='text-gray-400 text-xs mb-4'>
                Your Microsoft account session timed out. Sign in again to
                resume scanning.
              </p>
              <div className='flex gap-3'>
                <button
                  onClick={() =>
                    instance
                      .loginPopup({ scopes: ['User.Read'] })
                      .then(() => loadData())
                  }
                  className='px-4 py-2 bg-[#2ba6bb] text-white text-xs font-bold rounded-lg hover:bg-[#228a9d] transition-colors'
                >
                  Sign In with Microsoft
                </button>
                <button
                  onClick={() => setSessionExpired(false)}
                  className='px-4 py-2 border border-gray-700 text-gray-400 text-xs rounded-lg hover:text-gray-300 transition-colors'
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Banner — compact secondary indicator (always visible during scan) */}
        {isProcessing && (
          <div className='bg-white border border-[#2ba6bb]/20 rounded-2xl p-4 flex items-center gap-4 shadow-sm'>
            <Spinner size='medium' />
            <div className='flex-1 min-w-0'>
              <p className='font-bold text-[#186a78] text-sm'>
                {scanPhase === 'fetching' && 'Fetching pages from SharePoint…'}
                {scanPhase === 'analyzing' &&
                  `Analysing pages with Copilot AI… (${processedCount} processed so far)`}
                {scanPhase === 'saving' && 'Saving results to SharePoint…'}
                {scanPhase === 'idle' && 'Initialising scan…'}
              </p>
              <p
                className='text-[#228a9d] text-xs mt-0.5 font-medium truncate'
                title={progressMsg}
              >
                {progressMsg}
              </p>
            </div>
            {processedCount > 0 && (
              <span className='shrink-0 text-xs font-bold text-white bg-[#2ba6bb] px-3 py-1 rounded-full'>
                {processedCount} pages
              </span>
            )}
          </div>
        )}

        {/* Tab Nav */}
        <div className='bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden'>
          <div className='flex border-b border-gray-100 px-2 pt-2 overflow-x-auto'>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-1.5 px-3 sm:px-5 py-3 text-sm font-semibold rounded-t-xl border-b-2 mr-1 transition-all -mb-px whitespace-nowrap shrink-0
                                    ${
                                      activeTab === tab.id
                                        ? `${tab.activeColor} bg-gray-50/60`
                                        : 'border-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
              >
                {tab.icon}
                {tab.label}
                {tab.badge !== null && (
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full font-bold ml-0.5 ${activeTab === tab.id ? tab.badgeActive : 'bg-gray-100 text-gray-500'}`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
            <div className='flex-1' />
            <Tooltip
              content='Readiness = Structure (50%) + Readability (30%) + Heuristic baseline (20%). Coherence uses transformer sentence embeddings.'
              relationship='label'
            >
              <div className='hidden sm:flex items-center gap-1.5 px-4 text-xs text-gray-400 cursor-help hover:text-[#2ba6bb] transition-colors self-center shrink-0'>
                <Info16Regular /> How scoring works
              </div>
            </Tooltip>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'overview' && (
              <div className='p-5 space-y-5'>
                <ReadinessStoryCard items={items} />
                <QualityStrip items={items} />
                {items.length === 0 && !isProcessing && (
                  <div className='text-center py-20 text-gray-400'>
                    <ShieldCheckmark24Regular className='w-12 h-12 mx-auto text-gray-200 mb-3' />
                    <p className='font-semibold text-gray-600'>
                      No pages scanned yet
                    </p>
                    <p className='text-sm mt-1'>
                      Click <strong>Re-scan Pages</strong> above to start your
                      first AI readiness analysis.
                    </p>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'pages' && <CopilotDataTable items={items} />}
            {activeTab === 'debug' && <CopilotDebugInspector items={items} />}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
